import { union } from 'ramda';
import React, { FC, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { OrphanTasksInfo as OrphanTasksInfoMolecule } from 'components/molecules/documents/orphan-tasks';
import { TaskList } from 'components/molecules/tasks/task-list';
import { useDocuments } from 'components/organisms/document/document-store';
import { MinifiedTaskModal } from 'components/organisms/templates/task-modal';
import { DocumentResourceType } from 'data/documents';
import { Task } from 'data/tasks';
import { useTemplateMentionsData } from 'data/templates/hooks';
import { ProjectTemplateTask } from 'data/templates/projects';
import { useRocketlaneDocumentTasks } from '../use-rocketlane-document-tasks';
import { TaskScrollIntoView } from './scroll-into-view';

const Wrapper = styled.div`
  margin-bottom: 'var(--layout-03)';
`;

const OrphanTasksInfo = styled(OrphanTasksInfoMolecule)`
  margin: var(--layout-03) 0 var(--layout-01) 0;
`;

const findTaskFromResourceSection = (
  defaultResourceSectionTasks: any[],
  taskId?: number,
) => {
  return defaultResourceSectionTasks.find((t) => t?.taskId === taskId);
};

const EmptyFunction = () => {};

export const DefaultTaskSection: FC<{
  docId: number;
  readOnly?: boolean;
  createResourceSection(): void;
  handleAssigneeChange(
    taskId: number,
    selectedAssignee: any,
    resourceSectionId: number,
  ): void;
  handleNewTaskAdded({
    resourceType,
    taskName,
    resourceSectionId,
  }): Promise<void>;
  onDeleteTask(task: ProjectTemplateTask, resourceSectionId: number): void;
}> = ({
  docId,
  readOnly,
  createResourceSection,
  handleAssigneeChange,
  handleNewTaskAdded,
  onDeleteTask,
}) => {
  const [state] = useDocuments();
  const { members } = useTemplateMentionsData();

  const [activeResourceSectionId, setActiveResourceSectionId] =
    useState<number | null>(null);
  const [activeTask, setActiveTask] =
    useState<ProjectTemplateTask | null>(null);
  const [isModalVisible, toggleModalVisibility] = useState(false);
  const taskListRef = useRef<HTMLElement | null>(null);
  const [showOrphanTasks, setShowOrphanTasks] = useState(false);

  const { documentTasks, orphanTasks, defaultResourceSection } =
    useRocketlaneDocumentTasks({ docId });

  const renderExtraActions = (record) => {
    return record.resourceType === DocumentResourceType.Highlight ? (
      <TaskScrollIntoView
        activeEditor={state.editor}
        scrollId={record.resourceSectionId}
      />
    ) : null;
  };

  const handleDelete = (record) => {
    const task = findTaskFromResourceSection(documentTasks, record.taskId);

    onDeleteTask(
      record as unknown as ProjectTemplateTask,
      task.resourceSectionId,
    );
  };

  const onItemClick = ([, , taskId]) => {
    if (readOnly) {
      return;
    }
    const task = findTaskFromResourceSection(documentTasks, taskId);

    setActiveResourceSectionId(task?.resourceSectionId || null);
    setActiveTask(task as ProjectTemplateTask);
    toggleModalVisibility(true);
  };

  const onAssigneeChange = (taskId: number, assignee: any) => {
    const task = findTaskFromResourceSection(documentTasks, taskId);

    handleAssigneeChange(taskId, assignee, task.resourceSectionId);
  };

  const onAddNewTask = (taskName: string): Promise<void> => {
    if (!defaultResourceSection) {
      createResourceSection();
    }

    return handleNewTaskAdded({
      resourceType: DocumentResourceType.Default,
      taskName,
      resourceSectionId: defaultResourceSection?.resourceSectionId,
    });
  };

  const taskFromStore = useMemo(
    () =>
      findTaskFromResourceSection(
        union(documentTasks, orphanTasks),
        activeTask?.taskId,
      ) ?? (null as unknown as ProjectTemplateTask),
    [activeTask, documentTasks, orphanTasks],
  );

  return (
    <Wrapper>
      <h6>
        <FormattedMessage id="document.tasks" />
      </h6>
      <TaskList
        assignees={members}
        canAddDate={false}
        data={documentTasks as unknown[] as Task[]}
        disableStatusDropdown
        readOnly={readOnly}
        refTo={taskListRef}
        size="small"
        handleStatusChange={EmptyFunction}
        handleDueDateChange={EmptyFunction}
        onItemClick={onItemClick}
        handleAssigneeChange={onAssigneeChange}
        handleDelete={handleDelete}
        handleNewTaskAdded={onAddNewTask}
        onEnableTaskAddRow={createResourceSection}
        renderExtraActions={renderExtraActions}
      />
      <OrphanTasksInfo
        total={orphanTasks.length}
        isOrphanTasksVisible={showOrphanTasks}
        onOrphanTaskVisibilityChange={() =>
          setShowOrphanTasks(!showOrphanTasks)
        }
      />
      {showOrphanTasks && (
        <TaskList
          assignees={members}
          canAddDate={false}
          data={orphanTasks as unknown[] as Task[]}
          disableNewRow
          disableStatusDropdown
          readOnly
          refTo={taskListRef}
          size="small"
          handleStatusChange={EmptyFunction}
          handleDueDateChange={EmptyFunction}
          onItemClick={onItemClick}
          handleAssigneeChange={onAssigneeChange}
          handleDelete={handleDelete}
          handleNewTaskAdded={onAddNewTask}
          onEnableTaskAddRow={createResourceSection}
          renderExtraActions={renderExtraActions}
        />
      )}
      {activeResourceSectionId && taskFromStore != null && (
        <MinifiedTaskModal
          docId={docId}
          resourceSectionId={activeResourceSectionId}
          task={taskFromStore}
          visible={isModalVisible}
          onClose={() => toggleModalVisibility(false)}
        />
      )}
    </Wrapper>
  );
};
